/**
 * Custom hook for auto-saving configuration changes
 */

import { useEffect, useRef } from 'react';
import { updateLocalConfigurationByLocalId } from '../../../../../../../crud/management_api/local_configurations';
import { toast } from 'react-toastify';
import { getLocalId } from '../utils';
import type { UpdateConfigPayload } from '../utils';

interface UseAutoSaveParams {
  idioma: string;
  zonaHoraria: string;
  moneda: string;
  caja: string;
  isOpen: boolean;
  imprimirOrden: boolean;
  dafualtTip: string;
  selectedPaymentTypes: Set<number>;
  initialLoad: boolean;
}

export const useAutoSave = (params: UseAutoSaveParams): void => {
  const {
    idioma,
    zonaHoraria,
    moneda,
    caja,
    isOpen,
    imprimirOrden,
    dafualtTip,
    selectedPaymentTypes,
    initialLoad,
  } = params;

  const didMountRef = useRef(false);

  useEffect(() => {
    // While in initialLoad, don't save
    if (initialLoad) return;

    // First time initialLoad becomes false, mark ref and don't save
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    // On subsequent changes (real edits), save and show toast
    const handleSaveChanges = async () => {
      try {
        const localId = getLocalId();
        const updatedConfig: UpdateConfigPayload = {
          moneda_id: parseInt(moneda, 10),
          language_id: parseInt(idioma, 10),
          timezone_id: parseInt(zonaHoraria, 10),
          main_caja_id: parseInt(caja, 10),
          print_orders: imprimirOrden,
          local_open: isOpen,
          default_tip: parseInt(dafualtTip, 10),
          payment_types: Array.from(selectedPaymentTypes),
        };

        await updateLocalConfigurationByLocalId(localId, updatedConfig);
        toast.success('Datos actualizados');
      } catch (error) {
        console.error('Error updating local configuration:', error);
      }
    };

    handleSaveChanges();
  }, [idioma, zonaHoraria, moneda, caja, isOpen, imprimirOrden, dafualtTip, selectedPaymentTypes, initialLoad]);
};
